import { inject, Injectable, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Observable, filter, firstValueFrom, map, take, tap, shareReplay } from 'rxjs';
import { environment } from '../../../environments/environment.js';
import { HttpClient } from '@angular/common/http';
import { CurrencyUpdateRTM } from '@gains-com/realtime';
import { currencyUIConfigurations, CurrencyVM, networkUIConfigurations } from '../payments-common.js';
import { Currency, CurrencyId } from '@gains-com/shared';

@Injectable({
  providedIn: 'root',
})
export class CurrenciesViewService {
  private readonly _http = inject(HttpClient);

  private readonly _currencies = signal<CurrencyVM[] | null>(null);

  private _currencyOrCurrencyUpdateToVM = (update: CurrencyUpdateRTM | Currency): CurrencyVM => {
    return {
      ...update,
      ...currencyUIConfigurations[update.ticker],
      ...(update.blockchain_id ? networkUIConfigurations[update.blockchain_id] : {}),
      scale_factor: update.scale_factor,
      display_name: currencyUIConfigurations[update.ticker].ui_label ?? currencyUIConfigurations[update.ticker].ui_name,
      is_multi_network: (update as CurrencyUpdateRTM).is_multi_network ?? false,
    };
  };

  private _fetchCurrencies$ = () => {
    return this._http.get<Currency[]>(`${environment.apiUrl}/currencies`).pipe(
      take(1),
      tap((currencies) => {
        this._currencies.set(
          currencies
            .filter((c) => c.enable_purchases && currencyUIConfigurations[c.ticker])
            .map(this._currencyOrCurrencyUpdateToVM)
            .sort((a, b) => a.display_name.localeCompare(b.display_name)),
        );
      }),
    );
  };

  currencies$: Observable<CurrencyVM[]> = toObservable(this._currencies).pipe(
    tap(() => {
      // If the current value is null, fetch data
      if (this._currencies() === null) {
        this._fetchCurrencies$().subscribe();
      }
    }),
    filter((currencies) => currencies !== null),
    shareReplay(1),
  ) as Observable<CurrencyVM[]>;

  currency$(currencyId: CurrencyId): Observable<CurrencyVM | null> {
    return this.currencies$.pipe(map((currencies) => currencies.find((c) => c.currency_id === currencyId) ?? null));
  }

  currency(currencyId: CurrencyId): Promise<CurrencyVM | null> {
    return firstValueFrom(this.currency$(currencyId));
  }

  currencies(): Promise<CurrencyVM[]> {
    return firstValueFrom(this.currencies$);
  }

  isCurrencyEnabled(currency: CurrencyVM | CurrencyUpdateRTM): boolean {
    return (
      (currency.enable_purchases || currency.enable_redemptions) &&
      currencyUIConfigurations[currency.ticker] !== undefined
    );
  }

  // Method to update currencies from realtime integration
  updateCurrencies(currencies: CurrencyUpdateRTM[]) {
    this._currencies.set(currencies.filter((c) => c.enable_purchases).map(this._currencyOrCurrencyUpdateToVM));
  }

  updateCurrency(update: CurrencyUpdateRTM) {
    const currentCurrencies = this._currencies();
    if (currentCurrencies) {
      this._currencies.set(
        [
          ...currentCurrencies.filter((c) => c.currency_id !== update.currency_id),
          ...(this.isCurrencyEnabled(update) ? [this._currencyOrCurrencyUpdateToVM(update)] : []),
        ].sort((a, b) => a.display_name.localeCompare(b.display_name)),
      );
    }
  }
}
